<script lang="ts">
	import { run } from "svelte/legacy"

	import { ArrowBack, ArrowForward, HomeIcon } from "luxedo-icons"
	import { SearchInput } from "misc-ui"
	import { Toast } from "toaster"
	import { navigate } from "svelte-routing"

	interface Props {
		/**
		 * Called when user presses left or right navigation buttons.
		 * Will only render navigation buttons if this fn is passed.
		 */
		onNavigate?: (isLeft: boolean) => void
		/**
		 * Called when user submits a search input.
		 * Will only render search bar if this fn is passed.
		 */
		onSearch?: (input: string) => void
		searchPlaceholder?: string
		/**
		 * Amount of pages being rendered  (e.g., for a table showing only 10 of the entries)
		 */
		pageAmount?: number
		/**
		 * Current page being rendered (e.g., 3/10)
		 */
		pageNumber?: number
		hideBackBtn?: boolean
		heading: string
	}

	let {
		onNavigate = undefined,
		onSearch = undefined,
		searchPlaceholder = "Search",
		pageAmount = undefined,
		pageNumber = $bindable(undefined),
		hideBackBtn = false,
		heading,
	}: Props = $props()

	let inputPageNumber: number = $state(pageNumber + 1)

	function updatePageNumber(e: Event) {
		e.preventDefault()
		pageNumber = inputPageNumber - 1
		if (pageAmount && pageNumber >= pageAmount) {
			inputPageNumber = 1
			pageNumber = 0
			return Toast.error("Not enough pages.")
		}
	}

	run(() => {
		pageNumber > -1 &&
			(() => {
				inputPageNumber = pageNumber + 1
			})()
	})
</script>

<header>
	{#if !hideBackBtn}
		<button class="icon back-button" onclick={() => navigate("/admin/")}>
			<HomeIcon strokeColor="var(--color-text-light)" />Home
		</button>
	{/if}
	<h1>{heading}</h1>
	<div class="heading-filter">
		{#if onNavigate}
			{#if pageAmount !== undefined && pageNumber !== undefined}
				<div class="page-index">
					<form action="" onsubmit={updatePageNumber}>
						<input
							id="page-number-input"
							type="number"
							min="1"
							bind:value={inputPageNumber}
						/>
					</form>
					<span> / </span>
					{pageAmount}
				</div>
			{/if}
			<div class="button-row">
				<button
					class="only-icon small"
					title="Next Page"
					onclick={() => onNavigate(true)}
					style={pageNumber === 0 ? "visibility: hidden" : ""}
					><ArrowBack /></button
				>
				<button
					class="only-icon small"
					title="Last Page"
					onclick={() => onNavigate(false)}
					style={pageNumber >= pageAmount - 1 ? "visibility: hidden" : ""}
					><ArrowForward /></button
				>
			</div>
		{/if}
		{#if onSearch}
			<SearchInput {onSearch} placeholder={searchPlaceholder} />
		{/if}
	</div>
</header>

<style>
	form {
		display: inline;
	}

	header {
		height: var(--header-height);
		display: flex;
		flex-direction: row;
		background-color: var(--color-bg-dark);
		justify-content: center;
		align-items: center;
	}

	.back-button {
		position: absolute;
		left: 0;
	}

	.back-button :global(svg) {
		max-height: 2rem;
		width: 2rem;
		margin-right: 1rem;
	}

	.heading-filter {
		color: var(--color-text-light);
		position: absolute;
		right: 0;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
	}

	.page-index {
		margin-right: 1rem;
	}

	input#page-number-input {
		width: 3rem;
		padding: 0 0.25rem;
		background-color: unset;
		border: none;
		box-shadow: unset;
		color: var(--color-text-light);
		text-align: end;
	}

	input#page-number-input:focus-visible {
		border: none;
	}

	#page-number-input::-webkit-outer-spin-button,
	#page-number-input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	.heading-filter button :global(svg) {
		max-height: 1rem;
	}

	.heading-filter button:last-of-type {
		margin-right: 1rem;
	}

	h1 {
		color: var(--color-text-light);
		font-size: var(--text-h3);
	}
</style>
